
<script>
import { required, maxLength, email } from "@vuelidate/validators";
import { tel } from "@/utils/form-validators";
import FormFieldset from "@/components/molecules/form-fieldset";
import Button from "@/components/atoms/button";
import Form from "@/components/molecules/form";
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import FormRadio from "@/components/molecules/form-radio";
import FormSelect from "@/components/molecules/form-select";
import FormSelectOption from "@/components/molecules/form-select/form-select-option";
import FormSelectOptGroup from "@/components/molecules/form-select/form-select-optgroup";
import Captcha from "@/components/molecules/captcha";
import SuccessPopin from "@/components/organisms/forms/successPopin";
import FormError from "@/components/molecules/form/form-error";
import ButtonGroup from "@/components/molecules/button-group";
import { useForms } from "@/components/organisms/forms/mixin";

export default {
  name: "CommunForm",
  components: {
    Form,
    FormFieldset,
    ButtonGroup,
    FormSelectOptGroup,
    FormSelect,
    FormGroup,
    FormSelectOption,
    FormInput,
    Button,
    FormError,
    FormRadio,
    Captcha,
    SuccessPopin
  },
  props: {
    magasins: {
      type: Array,
      required: true
    },
    magasinId: {
      type: Number,
      required: false,
      default: 121
    },
    magasinName: {
      type: String,
      required: false,
      default: null
    },
    subject: {
      type: String,
      required: false,
      default: ""
    },
    ...formValidationProps()
  },
  emits: ["save"],
  setup(props) {
    const { redirectToPreviousPage } = useForms();
    const { captcha, v$, getState, getErrorMessage, checkValidation, states } =
      useFormValidation(props);

    return {
      redirectToPreviousPage,
      captcha,
      v$,
      getState,
      getErrorMessage,
      checkValidation,
      states
    };
  },
  computed: {
    otherMagasins() {
      if (!this.magasinId) {
        return this.magasins;
      }
      return this.magasins.filter((magasin) => {
        return parseInt(magasin.id) !== this.magasinId;
      });
    },
    currentMagasin() {
      return this.magasins.find((magasin) => {
        return parseInt(magasin.id) === this.magasinId;
      });
    }
  },
  validations() {
    return {
      form: {
        sujet: {
          required
        },
        objet: {
          required
        },
        id_magasin: {
          required
        },
        nom: {
          required
        },
        prenom: {
          required
        },
        civilite: {
          required
        },
        email: {
          required,
          email,
          maxLength: maxLength(60)
        },
        tel: {
          tel
        },
        demande: {
          required,
          maxLength: maxLength(4000)
        }
      }
    };
  },
  watch: {
    subject(newValue, oldValue) {
      this.form.sujet = this.getSubject(newValue);
    }
  },
  data() {
    return {
      form: {
        sujet: this.getSubject(this.subject),
        objet: "",
        reference: "",
        id_magasin: this.currentMagasin ? this.currentMagasin.id : "",
        civilite: "",
        nom: "",
        prenom: "",
        email: "",
        tel: "",
        demande: ""
      },
      captchaToken: null
    };
  },
  methods: {
    verifyCaptcha(token) {
      if (token) {
        this.captchaToken = token;
      }
      const model = {
        ...this.form,
        "g-captcha-response": this.captchaToken
      };
      this.$emit("save", model);
    },
    save() {
      if (this.checkValidation(this.v$)) {
        this.$refs.captcha.execute();
      }
    },
    getSubject(subject) {
      switch (subject.replace("commun-", "")) {
        case "pro":
          return "Devis et informations Services Pro";
        case "stand":
          return "Location de stand en Galerie";
        case "vehicule":
          return "Location de véhicule";
        case "fuel":
          return "Cora Fuel";
        case "livre":
          return "Commander un livre";
        case "mag":
          return "Question / remarque sur un magasin";
        case "question":
          return "Question / remarque sur un magasin";
        default:
          return "";
      }
    }
  }
};
</script>
<template>
  <div class="u-mb-xlg u-pl-md u-pr-md u-bgcolor-white u-pb-md">
    <br>
    <SuccessPopin
      v-if="state===states.SUCCESS"
      @hidden="redirectToPreviousPage()"
    />
    <FormError
      v-if="error"
      :error="error"
    />
    <br>
    <br>
    <Form
      class="u-mx-xxlg u-mx-lg@md"
      @submit.prevent="save()"
    >
      <FormFieldset
        center
        size="full"
      >
        <FormGroup
          :state="getState(v$,'sujet','form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="sujet"
            v-model.trim="form.sujet"
            type="text"
            autocomplete
            disabled
            :state="getState(v$,'sujet','form')"
            size="full"
            @blur="v$.form.sujet.$touch()"
            @input="v$.form.sujet.$reset()"
          >
            Sujet *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.form.sujet.required.$invalid">
                Merci de renseigner le sujet de votre demande
              </template>
              <template v-else>
                {{ getErrorMessage("sujet") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'objet','form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="objet"
            v-model.trim="form.objet"
            type="text"
            autocomplete
            :state="getState(v$,'objet','form')"
            size="full"
            @blur="v$.form.objet.$touch()"
            @input="v$.form.objet.$reset()"
          >
            Objet *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.form.objet.required.$invalid">
                Merci de renseigner l'objet de votre demande
              </template>
              <template v-else>
                {{ getErrorMessage("objet") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'reference','form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="reference"
            v-model.trim="form.reference"
            type="text"
            autocomplete
            :state="getState(v$,'reference','form')"
            size="full"
          >
            Référence
          </FormInput>
          <template #message>
            <p>
              {{ getErrorMessage("reference") }}
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'id_magasin', 'form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <label for="magasinReference">
            <h2 class="u-font-19 u-mb-xs">
              Votre magasin de référence*
            </h2>
          </label>
          <FormSelect
            id="magasinReference"
            v-model="form.id_magasin"
            :state="getState(v$,'id_magasin', 'form')"
            :default-value="'Sélectionnez un magasin'"
            size="full"
          >
            <FormSelectOptGroup
              v-if="currentMagasin"
              label="Vous êtes rattaché(e) au magasin"
            >
              <FormSelectOption :value="currentMagasin.id">
                {{ currentMagasin.attributes.magasin }}
              </FormSelectOption>
            </FormSelectOptGroup>
            <FormSelectOptGroup
              v-else-if="magasinName && magasinId"
              label="Vous êtes rattaché(e) au magasin"
            >
              <FormSelectOption disabled>
                {{ magasinName }}
              </FormSelectOption>
            </FormSelectOptGroup>
            <FormSelectOptGroup label="Autres magasins Cora">
              <FormSelectOption
                v-for="magasin in otherMagasins"
                :key="magasin.id"
                :value="magasin.id"
              >
                {{ magasin.attributes.magasin }}
              </FormSelectOption>
            </FormSelectOptGroup>
          </FormSelect>
          <template #message>
            <p>
              <template v-if="v$.form.id_magasin.required.$invalid">
                Merci de choisir un magasin
              </template>
              <template v-else>
                {{ getErrorMessage("id_magasin") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <label class="u-block u-font-13 u-mb-xs u-color-text-light">Civilité *</label>
        <FormGroup
          :state="getState(v$,'civilite', 'form')"
          inline
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormRadio
            v-model="form.civilite"
            name="civilite"
            value="Mme"
            class="u-mr-lg"
          >
            Madame
          </FormRadio>
          <FormRadio
            v-model="form.civilite"
            name="civilite"
            value="M."
          >
            Monsieur
          </FormRadio>
          <template #message>
            <p>
              <template v-if="v$.form.civilite.required.$invalid">
                Merci de renseigner votre civilité
              </template>
              <template v-else>
                {{ getErrorMessage("civilite") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'nom', 'form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="nom"
            v-model.trim="form.nom"
            :state="getState(v$,'nom', 'form')"
            maxlength="60"
            size="full"
            @blur="v$.form.nom.$touch()"
            @input="v$.form.nom.$reset()"
          >
            Nom *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.form.nom.required.$invalid">
                Merci de renseigner votre nom
              </template>
              <template v-else>
                {{ getErrorMessage("nom") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'prenom', 'form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="prenom"
            v-model.trim="form.prenom"
            :state="getState(v$,'prenom', 'form')"
            maxlength="40"
            size="full"
            @blur="v$.form.prenom.$touch()"
            @input="v$.form.prenom.$reset()"
          >
            Prénom *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.form.prenom.required.$invalid">
                Merci de renseigner votre prénom
              </template>
              <template v-else>
                {{ getErrorMessage("prenom") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'email','form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="email"
            v-model.trim="form.email"
            :state="getState(v$,'email','form')"
            size="full"
            @blur="v$.form.email.$touch()"
            @input="v$.form.email.$reset()"
          >
            Email *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.form.email.required.$invalid">
                Merci de confirmer votre adresse e-mail
              </template>
              <template v-else-if="v$.form.email.email.$invalid">
                Le format de cette adresse e-mail est invalide
              </template>
              <template v-else>
                {{ getErrorMessage("email") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'tel', 'form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="tel"
            v-model.trim="form.tel"
            :state="getState(v$,'tel', 'form')"
            size="full"
            @blur="v$.form.tel.$touch()"
            @input="v$.form.tel.$reset()"
          >
            Téléphone
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.form.tel.tel.$invalid">
                Ce numéro de téléphone est invalide
              </template>
              <template v-else>
                {{ getErrorMessage("tel") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'demande', 'form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="demande"
            v-model.trim="form.demande"
            type="textarea"
            :state="getState(v$,'demande', 'form')"
            size="full"
            @blur="v$.form.demande.$touch()"
            @input="v$.form.demande.$reset()"
          >
            Votre Demande *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.form.demande.required.$invalid">
                Merci de renseigner votre demande
              </template>
              <template v-else>
                {{ getErrorMessage("demande") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <Captcha
          ref="captcha"
          @verify="verifyCaptcha"
        />

        <ButtonGroup center>
          <Button
            variant="secondary"
            radius="full"
            type="submit"
            value="Je valide ma demande"
            size="md"
            :disabled="state === states.LOADING"
            class="u-mt-sm u-flex-justify-center@sm"
          />
        </ButtonGroup>
      </FormFieldset>
    </Form>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/inscription-form.scss";
</style>
